import { Button } from '@/components/ui/button';
import { Play, ArrowRight, CheckCircle } from 'lucide-react';
import heroImage from '@/assets/shooting-formal-event.jpg';

const Hero = () => {
  const trustBadges = [
    'Med Spas & Aesthetics',
    'Dental Practices',
    'Financial Advisors',
    'Real Estate Agents',
    '& More',
  ];

  return (
    <section className="relative min-h-[90vh] sm:min-h-screen flex items-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src={heroImage}
          alt="Professional video production studio"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground/95 via-foreground/85 to-foreground/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/50 to-transparent" />
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-primary/20 rounded-full blur-3xl animate-float hidden sm:block" />
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-secondary/20 rounded-full blur-3xl animate-float hidden sm:block" style={{ animationDelay: '2s' }} />

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-32 pb-16 sm:pb-20">
        <div className="max-w-4xl">
          {/* Eyebrow */}
          <div className="inline-flex items-center gap-2 bg-background/10 backdrop-blur-sm border border-background/20 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 mb-6 sm:mb-8 animate-fade-in">
            <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
            <span className="text-background/90 text-xs sm:text-sm font-medium">
              Central Ohio's Trusted Media Partner
            </span>
          </div>

          {/* Main Headline */}
          <h1 className="font-display font-extrabold text-3xl sm:text-5xl lg:text-6xl xl:text-7xl text-background leading-tight mb-4 sm:mb-6 animate-fade-in" style={{ animationDelay: '0.1s' }}>
            Columbus Ohio Video Production
            <span className="block mt-1 sm:mt-2">
              <span className="text-primary">& Social Media Agency</span>
            </span>
          </h1>

          {/* Subheadline */}
          <p className="text-base sm:text-xl lg:text-2xl text-background/80 max-w-2xl mb-6 sm:mb-8 leading-relaxed animate-fade-in" style={{ animationDelay: '0.2s' }}>
            Premium video production and social media management for professionals who demand results. We help you build trust and attract high-value clients.
          </p>

          {/* Trust Indicators */}
          <div className="flex flex-wrap gap-2 sm:gap-3 mb-8 sm:mb-10 animate-fade-in" style={{ animationDelay: '0.3s' }}>
            {trustBadges.map((badge) => (
              <div
                key={badge}
                className="flex items-center gap-1.5 sm:gap-2 bg-background/10 backdrop-blur-sm rounded-lg px-2.5 sm:px-4 py-1.5 sm:py-2"
              >
                <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
                <span className="text-background/90 text-xs sm:text-sm font-medium">{badge}</span>
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 animate-fade-in" style={{ animationDelay: '0.4s' }}>
            <Button variant="hero" size="xl" className="group" asChild>
              <a href="/booking">
                Book Your Discovery Call
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </Button>
            <Button variant="heroOutline" size="xl" className="group" asChild>
              <a href="/portfolio">
                <Play className="w-5 h-5" />
                View Our Work
              </a>
            </Button>
          </div>

          {/* Social Proof */}
          <div className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-background/20 animate-fade-in" style={{ animationDelay: '0.5s' }}>
            <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-8">
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-display font-bold text-primary">500+</div>
                <div className="text-background/70 text-xs sm:text-sm">Leads Generated</div>
              </div>
              <div className="w-px h-12 bg-background/20 hidden sm:block" />
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-display font-bold text-primary">150-400%</div>
                <div className="text-background/70 text-xs sm:text-sm">Increase in profile activity within 90 days of consistent short-form content</div>
              </div>
              <div className="w-px h-12 bg-background/20 hidden sm:block" />
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-display font-bold text-primary">1M+</div>
                <div className="text-background/70 text-xs sm:text-sm">Content Views</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator - hide on mobile */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce hidden sm:block">
        <div className="w-6 h-10 border-2 border-background/40 rounded-full flex justify-center pt-2">
          <div className="w-1.5 h-3 bg-background/60 rounded-full animate-pulse" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
